import React, { useEffect, useReducer, useState } from "react";
import { Filter } from "../../interfaces/filter";
import FilterContext from "./FilterContext";
import { FilterReducer } from "./FilterReducer";
import { useSearchParams } from "react-router-dom";
import useConfig from "../../hooks/config/useConfig";

interface props {
  children: JSX.Element | JSX.Element[];
}

let INITIAL_STATE: Filter = {};

export const FilterProvider = ({ children }: props) => {
  const [searchParams] = useSearchParams();
  const { allCountries } = useConfig();

  const getIntialState = () => {
    let searchTerms: string[] = [];

    searchParams.forEach((value: any, key) => {
      
      switch (key) {
        case "amenities":
          INITIAL_STATE.amenities = value.split(',').map((id: string) => parseInt(id))
          break;

        case "bathrooms":
          INITIAL_STATE.bathrooms = value.split(',')
          break;

        case "country":
          INITIAL_STATE.country = value.split(',').map((id: string) => parseInt(id))
          break;

        case "location":
          INITIAL_STATE.location = value.split(',').map((id: string) => parseInt(id))
          // Convert location IDs to names and add to search
          const locationIds = value.split(',');
          locationIds.forEach((id: string) => {
            const location = allCountries
              .flatMap(c => c.departments || [])
              .find(d => d.id.toString() === id);
            if (location?.name) {
              searchTerms.push(location.name);
            }
          });
          break;

        case "neighbourhood":
          INITIAL_STATE.neighbourhood = value.split(',').map((id: string) => parseInt(id))
          // Convert neighbourhood IDs to names and add to search
          const neighbourhoodIds = value.split(',');
          neighbourhoodIds.forEach((id: string) => {
            const neighbourhood = allCountries
              .flatMap(c => c.departments || [])
              .flatMap(d => d.zones || [])
              .find(z => z.id.toString() === id);
            if (neighbourhood?.name) {
              searchTerms.push(neighbourhood.name);
            }
          });
          break;
      
        case "commune":
          INITIAL_STATE.commune = value.split(',').map((id: string) => parseInt(id))
          // Convert commune IDs to names and add to search
          const communeIds = value.split(',');
          communeIds.forEach((id: string) => {
            const commune = allCountries
              .flatMap(c => c.departments || [])
              .flatMap(d => d.zones || [])
              .flatMap(z => z.communes || [])
              .find(c => c.id.toString() === id);
            if (commune?.name) {
              searchTerms.push(commune.name);
            }
          });
          break;

        case "minPrice":
          INITIAL_STATE.minPrice = parseFloat(value)
          break;

        case "maxPrice":
          INITIAL_STATE.maxPrice = parseFloat(value)
          break;

        case "propertyType":
          INITIAL_STATE.propertyType = value.split(',').map((id: string) => parseInt(id))
          break;

        case "rooms":
          INITIAL_STATE.rooms = value.split(',')
          break;

        case "state":
          INITIAL_STATE.state = value.split(',').map((id: string) => parseInt(id))
          break;

        case "order":
          // Ensure array form for order (comma-separated in URL)
          INITIAL_STATE.order = value.split(',')
          break;

        case "search":
          INITIAL_STATE.search = value.split(',')
          break;

        case "units_rented":
          INITIAL_STATE.units_rented = value === 'true' || value === true
          break;

        case "social_housing":
          INITIAL_STATE.social_housing = value === 'true' || value === true
          break;

        case "hand_over_date":
          INITIAL_STATE.hand_over_date = value.split(',')
          break;

        case "financing":
          INITIAL_STATE.financing = value.split(',')
          break;
        
        case "subsidy":
          INITIAL_STATE.subsidy = value.split(',')
          break;

        case "currency_minmax":
          INITIAL_STATE.currency_minmax = value
          break;

        case "is_promo":
          INITIAL_STATE.is_promo = value === 'true' || value === true
          break;
        case "developers":
          INITIAL_STATE.developers = value.split(',').map((id: string) => parseInt(id))
          break;
        default:
          break;
      }
    });

    // Merge existing search terms with location names
    const existingSearch = INITIAL_STATE.search || [];
    INITIAL_STATE.search = [...existingSearch, ...searchTerms].filter((term, index, arr) => 
      arr.indexOf(term) === index // Remove duplicates
    );

    // Ensure default order if none provided via URL
    if (!INITIAL_STATE.order) {
      // Use array form to keep consistency with Sorter and request body
      (INITIAL_STATE as any).order = ["promos","popularity"]
    }

    return INITIAL_STATE;
  };
  
  const [filterState, dispatch] = useReducer(FilterReducer, getIntialState());
  const initialCountry = parseInt(searchParams.get('country') || '') || 0
  const [ countryFilterID , setCountryFilterID] = useState(initialCountry)

  const setFilter = (filter: Filter) => {
    dispatch({ type: "setFilter", payload: filter });
  };

  const removeFilter = () => {
    dispatch({ type: "removeFilter" });
  };

  // Reconcile search tags once when countries are loaded
  // Avoid re-adding names on every search change (which breaks deselection)
  const [hasReconciled, setHasReconciled] = useState(false);
  useEffect(() => {
    if (hasReconciled) return;
    if (!allCountries || allCountries.length === 0) return;

    const namesToAdd: string[] = [];

    const addNamesFromIds = (idsCsv: string | null, resolver: () => string[]) => {
      if (!idsCsv) return;
      const names = resolver();
      names.forEach(n => {
        if (n && !namesToAdd.includes(n)) namesToAdd.push(n);
      });
    };

    const locParam = searchParams.get('location');
    addNamesFromIds(locParam, () => {
      const ids = (locParam || '').split(',').filter(Boolean);
      return allCountries
        .flatMap(c => c.departments || [])
        .filter(d => ids.includes(String(d.id)))
        .map(d => d.name || '')
        .filter(Boolean);
    });

    const neighParam = searchParams.get('neighbourhood');
    addNamesFromIds(neighParam, () => {
      const ids = (neighParam || '').split(',').filter(Boolean);
      return allCountries
        .flatMap(c => c.departments || [])
        .flatMap(d => d.zones || [])
        .filter(z => ids.includes(String(z.id)))
        .map(z => z.name || '')
        .filter(Boolean);
    });

    const communeParam = searchParams.get('commune');
    addNamesFromIds(communeParam, () => {
      const ids = (communeParam || '').split(',').filter(Boolean);
      return allCountries
        .flatMap(c => c.departments || [])
        .flatMap(d => d.zones || [])
        .flatMap(z => z.communes || [])
        .filter(co => ids.includes(String(co.id)))
        .map(co => co.name || '')
        .filter(Boolean);
    });

    // Merge with existing search (URL search param already parsed earlier)
    const existing = Array.isArray(filterState.search) ? filterState.search : [];
    const merged = [...existing];
    namesToAdd.forEach(n => {
      if (!merged.map(s => s.toLowerCase()).includes(n.toLowerCase())) merged.push(n);
    });

    if (merged.length !== existing.length) {
      setFilter({ search: merged });
    }
    setHasReconciled(true);
  }, [allCountries, hasReconciled, searchParams]);

  return (
    <FilterContext.Provider
      value={{ filterState, setFilter, removeFilter, setCountryFilterID, countryFilterID }}
    >
      {children}
    </FilterContext.Provider>
  );
};
